import { BalanceData, Rarity } from "../data/balance";
import { OfferItem } from "../data/offers";
import { ShopStockEntry } from "../data/shops";
import { Fix64 } from "../maths/fix64";
import { Pcg32 } from "../maths/pcg32";
import { DealTarget, GrantedItems, grantItems } from "./deals";
import { GearItem, generateGear } from "./gear";
import { sellPrice } from "./market";
import { merchantFreeIndex, withFreeSlot } from "./merchantFree";
import { GemPrices, gemPrices, itemGems } from "./offerValue";
import { metaSeed, utcDay, utcWeek } from "./orders";
import { Currency, WalletReason } from "./wallet";

/**
 * The three currency shops (docs/SHOP.md §9; balance/src/shops.json): the Mystery Merchant (Marks; `slots` slots a UTC
 * day rolled from (player, day, refresh), Left: 1 each; Refresh for gems after the VIP's free ones), the Event shop
 * (Marks too since Valor merged into them, 2026-09-30; featured slots picked by (player, ISO week) at `featured.off`, and fixed stock with weekly limits) and the VIP
 * shop (gems; weekly limits; entries from their VIP level). Honest prices: base = the item's gem price (docs/SHOP.md
 * §1.2) × the currency's rate — a Merchant gear slot is priced exactly as the Trading Post priced it — and price =
 * floor(base × (100 − off) / 100), at least 1; the badge shows `off`. Pure and deterministic: a slot shown is the
 * slot sold (a purchase re-rolls the stock and refuses when the period, the refresh or the price moved on).
 */

export const SHOP_IDS = ["merchant", "event", "vip"] as const;
export type ShopId = (typeof SHOP_IDS)[number];
/** The currency shops spend gems or Marks (MP-23: Trophies have their own Hall, meta/trophyHall.ts). */
export type ShopCurrency = Exclude<Currency, "trophies">;
export const SHOP_CURRENCY: Readonly<Record<ShopId, ShopCurrency>> = { merchant: "marks", event: "marks", vip: "gems" };
/** The wallet reason a shop's spend is logged under. */
export const SHOP_SPEND_REASON: Readonly<Record<ShopId, WalletReason>> = { merchant: "merchant", event: "eventShop", vip: "vipShop" };

const PERCENT = 100;
/** Pcg32 streams, apart from every other use of the same seeds. */
const MERCHANT_STREAM = 431;
const FEATURED_STREAM = 433;
/** A rolled gear item's id until it is bought (the profile gives it a real one). */
const OFFER_ITEM_ID = 0;
/** Merchant rolls per UTC day in the seed (refreshes a day are far fewer). */
const ROLL_STRIDE = 64;

export interface ShopSlot {
  readonly shop: ShopId;
  readonly index: number;
  /** What the slot sells: an offer item (docs/SHOP.md _items), or null for a Merchant gear slot (see `gear`). */
  readonly item: OfferItem | null;
  /** A Merchant gear slot's item as it will arrive (its id is given on purchase). */
  readonly gear: GearItem | null;
  readonly currency: ShopCurrency;
  /** The documented base price in `currency`. */
  readonly basePrice: number;
  /** Discount percent against the base (0 = none); the badge shows it from shops.json showOffFromPercent. */
  readonly off: number;
  readonly price: number;
  /** Purchases per restock (1 for a Merchant slot). */
  readonly limit: number;
  readonly featured: boolean;
  /** VIP shop: the VIP level the entry opens at (0 otherwise). */
  readonly vipLevel: number;
  /** Restock period: the UTC day (Merchant) or UTC week (Event, VIP). */
  readonly period: number;
  /** Merchant: which roll of the day (0 = the morning's, +1 per refresh); 0 otherwise. */
  readonly roll: number;
  /** The Merchant's free item of the day (MP-08, meta/merchantFree.ts): price 0. */
  readonly free?: boolean;
}

/**
 * Marks for a gem value at `level` (docs/SHOP.md §9.1): marksPerGem × (1 + level / marksLevelDivisor), the Trading
 * Post's level scaling. Shop prices and event play's Marks (shops.json event.sources) both use it, so a Mark buys the
 * same in the Merchant and the Event shop, and a week of event play buys the same at every level.
 */
export function marksForGems(balance: BalanceData, gems: Fix64, level: number): number {
  const r = balance.shops.rates;
  return gems.mulInt(r.marksPerGem).mulInt(r.marksLevelDivisor + Math.max(0, level)).divInt(r.marksLevelDivisor).floorToInt();
}

/** The base price of an item in a currency (docs/SHOP.md §9.1): its gem price × the rate (Marks level-scaled). */
export function shopBasePrice(balance: BalanceData, currency: ShopCurrency, item: OfferItem, level: number, prices: GemPrices = gemPrices(balance)): number {
  const gems = itemGems(balance, item, prices);
  return currency === "gems" ? gems.floorToInt() : marksForGems(balance, gems, level);
}

/** A price after a discount: floor(base × (100 − off) / 100), at least 1. */
export function offPrice(base: number, off: number): number { return Math.max(1, Math.floor((base * (PERCENT - off)) / PERCENT)); }

/** Whether a slot's discount badge shows. */
export function showsOff(balance: BalanceData, off: number): boolean { return off > 0 && off >= balance.shops.showOffFromPercent; }

function weighted<T>(entries: readonly T[], weightOf: (e: T) => number, rng: Pcg32): T | null {
  const total = entries.reduce((a, e) => a + weightOf(e), 0);
  if (total <= 0) return null;
  let r = rng.nextInt(total);
  for (const e of entries) { r -= weightOf(e); if (r < 0) return e; }
  return entries[entries.length - 1];
}

// ------------------------------------------------------------------ Mystery Merchant

/** The Merchant's slots on UTC `day`, roll `roll` (0 = the morning's; +1 per refresh), for a player at `level`. */
export function merchantStock(balance: BalanceData, playerId: string, day: number, roll: number, level: number): ShopSlot[] {
  const m = balance.shops.merchant, mk = balance.commerce.market, L = Math.max(1, level), prices = gemPrices(balance);
  if (m.slots <= 0) return [];
  const rng = new Pcg32(metaSeed(playerId, "merchant", day * ROLL_STRIDE + roll), MERCHANT_STREAM);
  const pool = m.pool.filter((e) => e.minLevel <= L);
  const out: ShopSlot[] = [];
  for (let index = 0; index < m.slots; index++) {
    const off = weighted(m.discounts, (d) => d.weight, rng)?.off ?? 0;
    if (index < m.gearSlots) {
      const rarity = (weighted(mk.stockWeights.map((w, r) => ({ r, w })), (x) => x.w, rng)?.r ?? Rarity.Rare) as Rarity;
      const gear = generateGear(balance, OFFER_ITEM_ID, L, rarity, rng);
      const base = Fix64.fromInt(sellPrice(balance, gear)).mul(mk.buyMarkup).floorToInt();
      out.push({ shop: "merchant", index, item: null, gear, currency: "marks", basePrice: base, off, price: offPrice(base, off), limit: 1, featured: false, vipLevel: 0, period: day, roll });
      continue;
    }
    const e = weighted(pool, (x) => x.weight, rng);
    if (!e) continue;
    const base = shopBasePrice(balance, "marks", e.item, L, prices);
    out.push({ shop: "merchant", index, item: e.item, gear: null, currency: "marks", basePrice: base, off, price: offPrice(base, off), limit: 1, featured: false, vipLevel: 0, period: day, roll });
  }
  return out;
}

/** What the next Merchant refresh today costs: 0 while VIP free refreshes are left, then refreshGems in order; null when none is left. */
export function merchantRefreshCost(balance: BalanceData, freeUsed: number, paidUsed: number, freeRefreshes: number): number | null {
  if (freeUsed < freeRefreshes) return 0;
  return balance.shops.merchant.refreshGems[paidUsed] ?? null;
}

// ------------------------------------------------------------------ Event shop

/** The week's featured items: `featured.slots` distinct entries of the pool, picked by (player, week). */
export function eventFeatured(balance: BalanceData, playerId: string, week: number): OfferItem[] {
  const f = balance.shops.event.featured, rng = new Pcg32(metaSeed(playerId, "eventFeatured", week), FEATURED_STREAM);
  const left = [...f.pool], out: OfferItem[] = [];
  while (out.length < f.slots && left.length > 0) out.push(left.splice(rng.nextInt(left.length), 1)[0]);
  return out;
}

function stockSlot(balance: BalanceData, shop: ShopId, index: number, e: ShopStockEntry, week: number, level: number, prices: GemPrices): ShopSlot {
  const currency = SHOP_CURRENCY[shop], base = shopBasePrice(balance, currency, e.item, level, prices);
  return { shop, index, item: e.item, gear: null, currency, basePrice: base, off: e.off, price: offPrice(base, e.off), limit: e.limit, featured: false, vipLevel: e.vipLevel, period: week, roll: 0 };
}

/** The Event shop in UTC `week`: the featured slots first, then the stock the player's level shows. */
export function eventStock(balance: BalanceData, playerId: string, week: number, level: number): ShopSlot[] {
  const ev = balance.shops.event, prices = gemPrices(balance), L = Math.max(1, level);
  const featured = eventFeatured(balance, playerId, week).map((item, index): ShopSlot => {
    const base = shopBasePrice(balance, SHOP_CURRENCY.event, item, L, prices);
    return { shop: "event", index, item, gear: null, currency: SHOP_CURRENCY.event, basePrice: base, off: ev.featured.off, price: offPrice(base, ev.featured.off), limit: 1, featured: true, vipLevel: 0, period: week, roll: 0 };
  });
  const n = featured.length;
  return [...featured, ...ev.stock.map((e, i) => ({ e, i })).filter(({ e }) => e.minLevel <= L).map(({ e, i }) => stockSlot(balance, "event", n + i, e, week, L, prices))];
}

/** The VIP shop in UTC `week`: every entry (the page shows the locked ones greyed; `buyShopSlot` refuses them). */
export function vipShopStock(balance: BalanceData, week: number): ShopSlot[] {
  const prices = gemPrices(balance);
  return balance.shops.vip.stock.map((e, i) => stockSlot(balance, "vip", i, e, week, 1, prices));
}

/** The restock period of a shop at `now`: the UTC day (Merchant) or UTC week. */
export function shopPeriod(shop: ShopId, now: number): number { return shop === "merchant" ? utcDay(now) : utcWeek(utcDay(now)); }

// ------------------------------------------------------------------ the `shops` save section

export interface ShopsJson {
  merchant: { day: number; roll: number; freeUsed: number; paidUsed: number; bought: number[] };
  event: { week: number; bought: number[] };
  vip: { week: number; bought: number[] };
  /** Event Marks from farm wins: the UTC day and the wins paid on it (shops.json farmWinsPerDay). */
  farm: { day: number; wins: number };
  /** The UTC day the Merchant's free item was last taken (MP-08; absent before it). */
  merchantFree?: number;
}

const isInt = (n: unknown): n is number => Number.isInteger(n);
const counts = (v: unknown): number[] => (Array.isArray(v) ? v.map((n) => (Number.isInteger(n) && n >= 0 ? n : 0)) : []);

/** What was bought this period in each shop, the Merchant's refreshes today and the farm wins paid Marks today. */
export class ShopsState {
  merchant = { day: -1, roll: 0, freeUsed: 0, paidUsed: 0, bought: [] as number[] };
  event = { week: -1, bought: [] as number[] };
  vip = { week: -1, bought: [] as number[] };
  farm = { day: -1, wins: 0 };
  merchantFreeDay = -1;

  /** Moves each shop to `now`'s period (a new period restocks: nothing bought, no refresh used). */
  roll(now: number): void {
    const day = utcDay(now), week = utcWeek(day);
    if (day > this.merchant.day) this.merchant = { day, roll: 0, freeUsed: 0, paidUsed: 0, bought: [] };
    if (week > this.event.week) this.event = { week, bought: [] };
    if (week > this.vip.week) this.vip = { week, bought: [] };
  }

  /** The Merchant's current roll today (0 = the morning's). */
  merchantRoll(now: number): number { this.roll(now); return this.merchant.roll; }

  /** Purchases of a slot this period. */
  boughtCount(shop: ShopId, index: number, now: number): number { this.roll(now); return this[shop].bought[index] ?? 0; }

  noteBought(shop: ShopId, index: number, now: number): void {
    this.roll(now);
    const b = this[shop].bought;
    while (b.length <= index) b.push(0);
    b[index]++;
  }

  /** Records a Merchant refresh (free or paid): the day's next roll, every slot back. */
  noteRefresh(now: number, free: boolean): void {
    this.roll(now);
    this.merchant.roll++;
    if (free) this.merchant.freeUsed++; else this.merchant.paidUsed++;
    this.merchant.bought = [];
  }

  /** Marks for a farm win now at `level`: `farmWin`'s worth for the first farmWinsPerDay farm wins of a UTC day, else 0 (counted). */
  farmMarks(balance: BalanceData, now: number, level: number): number {
    const s = balance.shops.event.sources, day = utcDay(now);
    if (day !== this.farm.day) this.farm = { day, wins: 0 };
    const marks = marksForGems(balance, s.farmWin, level);
    if (this.farm.wins >= s.farmWinsPerDay || marks <= 0) return 0;
    this.farm.wins++;
    return marks;
  }

  toJson(): ShopsJson {
    return { merchant: { ...this.merchant, bought: [...this.merchant.bought] }, event: { ...this.event, bought: [...this.event.bought] }, vip: { ...this.vip, bought: [...this.vip.bought] }, farm: { ...this.farm }, merchantFree: this.merchantFreeDay };
  }

  /** Lenient restore, shop by shop: a malformed part reads as new (restocked). */
  static fromJson(j: unknown): ShopsState {
    const s = new ShopsState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<ShopsJson>;
    const m = src.merchant;
    if (m && isInt(m.day) && isInt(m.roll) && isInt(m.freeUsed) && isInt(m.paidUsed)) s.merchant = { day: m.day, roll: m.roll, freeUsed: m.freeUsed, paidUsed: m.paidUsed, bought: counts(m.bought) };
    for (const k of ["event", "vip"] as const) {
      const w = src[k];
      if (w && isInt(w.week)) s[k] = { week: w.week, bought: counts(w.bought) };
    }
    if (src.farm && isInt(src.farm.day) && isInt(src.farm.wins)) s.farm = { day: src.farm.day, wins: src.farm.wins };
    if (isInt(src.merchantFree)) s.merchantFreeDay = src.merchantFree;
    return s;
  }
}

// ------------------------------------------------------------------ buying

/** Everything a shop purchase touches. `vipLevel` gates the VIP shop; `gearSlots` fill a `gear` item with slot `pick`. */
export interface ShopBuyContext {
  readonly target: DealTarget;
  readonly level: number;
  readonly vipLevel: number;
  readonly gearSlots?: readonly number[];
}

export interface ShopPurchase { readonly slot: ShopSlot; readonly granted: GrantedItems; readonly gear: GearItem | null }

/** The shop's slots now, as the player sees them. */
export function shopStock(balance: BalanceData, state: ShopsState, shop: ShopId, playerId: string, now: number, level: number): ShopSlot[] {
  const period = shopPeriod(shop, now);
  switch (shop) {
    case "merchant": return withFreeSlot(merchantStock(balance, playerId, period, state.merchantRoll(now), level), state.merchantFreeDay === period ? -1 : merchantFreeIndex(balance, playerId, period));
    case "event": return eventStock(balance, playerId, period, level);
    case "vip": return vipShopStock(balance, period);
  }
}

/**
 * Buys a slot as shown: refused when the stock moved on (period, Merchant roll or price), the slot is sold out, the
 * VIP level is too low, or the currency is short. Pays the currency (wallet reason SHOP_SPEND_REASON), then the item:
 * a Merchant gear slot's item as shown (unbound, as the Trading Post sold it), anything else through grantItems (gear
 * bound; chests returned unbanked for the caller to bank). The caller persists.
 */
export function buyShopSlot(balance: BalanceData, state: ShopsState, shown: ShopSlot, now: number, ctx: ShopBuyContext): ShopPurchase | string {
  const t = ctx.target;
  const slot = shopStock(balance, state, shown.shop, t.playerId, now, ctx.level).find((s) => s.index === shown.index);
  if (!slot || slot.period !== shown.period || slot.roll !== shown.roll || slot.price !== shown.price) return "offer changed";
  if (state.boughtCount(slot.shop, slot.index, now) >= slot.limit) return "sold out";
  if (slot.shop === "vip" && ctx.vipLevel < slot.vipLevel) return "vip level too low";
  const ref = `${slot.shop}${slot.index}@${slot.period}.${slot.roll}`;
  const paid = t.wallet.spend(slot.currency, slot.price, SHOP_SPEND_REASON[slot.shop], now, ref);
  if (paid !== true) return paid;
  state.noteBought(slot.shop, slot.index, now);
  if (slot.free) state.merchantFreeDay = slot.period;
  if (slot.gear) {
    const gear = t.profile.receiveGear(slot.gear);
    return { slot, gear, granted: { gems: 0, gold: 0, speedUps: [], chests: [], gear: [gear], skins: [] } };
  }
  return { slot, gear: null, granted: grantItems(balance, { ...t, ref }, [slot.item!], ctx.gearSlots ?? []) };
}

/**
 * Refreshes the Merchant (every slot re-rolled, all back in stock): free while the VIP level's free refreshes are
 * left today, then for refreshGems in order (wallet reason `merchantRefresh`). Returns the gems paid, or why not.
 */
export function refreshMerchant(balance: BalanceData, state: ShopsState, t: DealTarget, now: number, freeRefreshes: number): number | string {
  state.roll(now);
  const cost = merchantRefreshCost(balance, state.merchant.freeUsed, state.merchant.paidUsed, freeRefreshes);
  if (cost === null) return "no refresh left today";
  if (cost > 0) {
    const paid = t.wallet.spend("gems", cost, "merchantRefresh", now, `merchant@${state.merchant.day}.${state.merchant.roll + 1}`);
    if (paid !== true) return paid;
  }
  state.noteRefresh(now, cost === 0);
  return cost;
}
